<script lang="ts">
  import { ACCENT_PALETTE, PASSWORD_MIN_LENGTH } from "@piano/shared";
  import { api } from "../lib/api/index.js";
  import { router } from "../lib/router.svelte.js";
  import { hue } from "../lib/stores/hue.svelte.js";
  import { library } from "../lib/stores/library.svelte.js";
  import { live } from "../lib/stores/live.svelte.js";
  import { session } from "../lib/stores/session.svelte.js";
  import { ui } from "../lib/stores/ui.svelte.js";
  import Icon from "../components/Icon.svelte";
  import Sheet from "../components/Sheet.svelte";
  import HuePickerSheet from "../components/hue/HuePickerSheet.svelte";
  import HueSetupSheet from "../components/hue/HueSetupSheet.svelte";

  let passwordOpen = $state(false);
  let current = $state("");
  let next = $state("");
  let repeat = $state("");
  let busy = $state(false);

  async function changePassword(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (next !== repeat) return ui.toast("Die neuen Passwörter stimmen nicht überein", "error");
    busy = true;
    try {
      await api.changePassword(current, next);
      ui.toast("Passwort geändert – andere Geräte wurden abgemeldet", "success");
      passwordOpen = false;
      current = next = repeat = "";
    } catch (error) {
      ui.error(error);
    } finally {
      busy = false;
    }
  }

  const penOnly = $derived(live.settings.annotations.inputMode === "pen-only");
  async function togglePenOnly(): Promise<void> {
    try {
      live.settings = await api.patchSettings({ annotations: { inputMode: penOnly ? "both" : "pen-only" } });
    } catch (error) {
      ui.error(error);
    }
  }

  // Licht (Philips Hue)
  let hueSetup = $state(false);
  let huePicker = $state(false);
  void hue.loadStatus();
  const hueSummary = $derived.by(() => {
    const pins = hue.status?.pins ?? [];
    const scenes = pins.filter((p) => p.type === "scene").length;
    const lamps = pins.length - scenes;
    if (!pins.length) return "Noch nichts ausgewählt";
    return [
      scenes && `${scenes} ${scenes === 1 ? "Szene" : "Szenen"}`,
      lamps && `${lamps} ${lamps === 1 ? "Lampe" : "Lampen"}`,
    ]
      .filter(Boolean)
      .join(", ");
  });
  async function disconnectHue(): Promise<void> {
    const ok = await ui.confirm({
      title: "Hue-Verbindung trennen?",
      message:
        "Der Licht-Knopf verschwindet aus dem Notenständer. Andere Nutzer behalten ihre Verbindung. " +
        "Den App-Eintrag auf der Bridge entfernst du bei Bedarf in der Hue-App unter Einstellungen → Apps.",
      confirmLabel: "Trennen",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.hueDisconnect();
      await hue.loadStatus();
    } catch (error) {
      ui.error(error);
    }
  }

  // Kategorien verwalten
  let categoryEdit = $state<{ id: string; name: string; color: string; icon: string } | null>(null);
  async function saveCategory(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (!categoryEdit) return;
    try {
      await library.updateCategory(categoryEdit.id, {
        name: categoryEdit.name.trim(),
        color: categoryEdit.color,
        icon: categoryEdit.icon.trim() || "♪",
      });
      categoryEdit = null;
    } catch (error) {
      ui.error(error);
    }
  }
  async function deleteCategory(): Promise<void> {
    if (!categoryEdit) return;
    const ok = await ui.confirm({
      title: "Kategorie löschen?",
      message: `„${categoryEdit.name}“ wird von allen Stücken entfernt. Die Noten bleiben erhalten.`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    await library.deleteCategory(categoryEdit.id).catch((error: unknown) => ui.error(error));
    categoryEdit = null;
  }
</script>

<div class="profile" class:narrow={!ui.wide}>
  <header>
    <span class="avatar">{(session.user?.displayName ?? "?").charAt(0).toUpperCase()}</span>
    <div>
      <h1>{session.user?.displayName}</h1>
      <span class="t2">{session.user?.email} · {session.isAdmin ? "Administrator" : "Mitglied"}</span>
    </div>
  </header>

  <section class="group">
    <button type="button" class="item" onclick={() => (passwordOpen = true)}
      ><Icon name="key" /> Passwort ändern</button
    >
    <button type="button" class="item" role="switch" aria-checked={penOnly} onclick={togglePenOnly}>
      <Icon name="pencilInput" />
      <span class="grow"
        >Notizen nur mit dem Apple Pencil<small class="t3">Finger scrollen dann weiter durch die Noten</small
        ></span
      >
      <span class="switch" aria-checked={penOnly}></span>
    </button>
  </section>

  <section class="block">
    <span class="label">Licht</span>
    <div class="group">
      {#if hue.connected && hue.status?.bridge}
        <div class="item static">
          <Icon name="bridge" />
          <span class="grow"
            >{hue.status.bridge.name}<small class="t3"
              ><span class="dot" class:off={!hue.status.bridge.reachable}></span>{hue.status.bridge.reachable
                ? "Philips Hue Bridge · verbunden"
                : "Bridge gerade nicht erreichbar"}</small
            ></span
          >
        </div>
        <button type="button" class="item" onclick={() => (huePicker = true)}>
          <Icon name="bulb" />
          <span class="grow">Im Notenständer<small class="t3">{hueSummary}</small></span>
          <Icon name="forward" size={18} />
        </button>
        <button type="button" class="item danger" onclick={disconnectHue}>
          <Icon name="close" /> <span class="grow">Verbindung trennen</span>
        </button>
      {:else if hue.away}
        <div class="item static">
          <Icon name="bulb" />
          <span class="grow"
            >Philips Hue<small class="t3"
              >Nur im Heimnetz verfügbar – unterwegs bleibt das Licht gesperrt</small
            ></span
          >
        </div>
      {:else}
        <button type="button" class="item" onclick={() => (hueSetup = true)}>
          <Icon name="bulb" />
          <span class="grow"
            >Philips Hue verbinden<small class="t3">Szenen direkt vom Notenständer wechseln</small></span
          >
          <Icon name="forward" size={18} />
        </button>
      {/if}
    </div>
  </section>

  <section class="block">
    <span class="label">Kategorien</span>
    <div class="group">
      {#each library.categories as category (category.id)}
        <button type="button" class="item" onclick={() => (categoryEdit = { ...category })}>
          <span class="swatch" style:background={category.color}>{category.icon}</span>
          <span class="grow">{category.name}</span>
          <Icon name="forward" size={18} />
        </button>
      {:else}
        <p class="t3 pad">Noch keine Kategorien – lege sie im Aktionsmenü eines Stücks an.</p>
      {/each}
    </div>
  </section>

  {#if session.isAdmin}
    <section class="block">
      <span class="label">Verwaltung</span>
      <div class="group">
        <button type="button" class="item" onclick={() => router.go("/verwaltung")}>
          <Icon name="users" /> <span class="grow">Nutzer, Statistik &amp; Bibliothek</span>
          <Icon name="forward" size={18} />
        </button>
      </div>
    </section>
  {/if}

  <button type="button" class="btn btn-ghost logout" onclick={() => session.logout()}
    ><Icon name="logout" size={18} /> Abmelden</button
  >
</div>

{#if passwordOpen}
  <Sheet title="Passwort ändern" onclose={() => (passwordOpen = false)} width={420}>
    <form class="form" onsubmit={changePassword}>
      <h2>Passwort ändern</h2>
      <label class="field"
        >Aktuelles Passwort<input
          class="input"
          type="password"
          autocomplete="current-password"
          bind:value={current}
          required
        /></label
      >
      <label class="field"
        >Neues Passwort (mind. {PASSWORD_MIN_LENGTH} Zeichen)<input
          class="input"
          type="password"
          autocomplete="new-password"
          minlength={PASSWORD_MIN_LENGTH}
          bind:value={next}
          required
        /></label
      >
      <label class="field"
        >Neues Passwort wiederholen<input
          class="input"
          type="password"
          autocomplete="new-password"
          bind:value={repeat}
          required
        /></label
      >
      <div class="actions">
        <button type="button" class="btn btn-ghost" onclick={() => (passwordOpen = false)}>Abbrechen</button>
        <button type="submit" class="btn btn-primary" disabled={busy}>Ändern</button>
      </div>
    </form>
  </Sheet>
{/if}

{#if hueSetup}
  <HueSetupSheet
    onclose={() => (hueSetup = false)}
    ondone={(adjust) => {
      hueSetup = false;
      huePicker = adjust;
    }}
  />
{/if}

{#if huePicker}
  <HuePickerSheet onclose={() => (huePicker = false)} />
{/if}

{#if categoryEdit}
  <Sheet title="Kategorie bearbeiten" onclose={() => (categoryEdit = null)} width={420}>
    <form class="form" onsubmit={saveCategory}>
      <h2>Kategorie bearbeiten</h2>
      <label class="field"
        >Name<input class="input" bind:value={categoryEdit.name} maxlength="120" required /></label
      >
      <label class="field"
        >Symbol (Emoji)<input class="input emoji" bind:value={categoryEdit.icon} maxlength="16" /></label
      >
      <div class="field">
        Farbe
        <div class="palette">
          {#each ACCENT_PALETTE as color (color)}
            <button
              type="button"
              class="color"
              style:background={color}
              aria-label="Farbe {color}"
              aria-pressed={categoryEdit.color.toUpperCase() === color}
              onclick={() => categoryEdit && (categoryEdit.color = color)}
            ></button>
          {/each}
        </div>
      </div>
      <div class="actions">
        <button type="button" class="btn btn-danger" onclick={deleteCategory}
          ><Icon name="trash" size={18} /> Löschen</button
        >
        <span class="grow"></span>
        <button type="submit" class="btn btn-primary">Speichern</button>
      </div>
    </form>
  </Sheet>
{/if}

<style>
  .profile {
    padding: calc(34px + var(--safe-top)) 32px 40px;
    display: flex;
    flex-direction: column;
    gap: 22px;
    max-width: 680px;
  }
  .narrow {
    padding: calc(20px + var(--safe-top)) 18px 24px;
  }
  header {
    display: flex;
    align-items: center;
    gap: 20px;
  }
  .avatar {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: var(--raised-2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--font-display);
    font-size: 34px;
    font-weight: 800;
  }
  h1 {
    font-size: 32px;
  }
  header span {
    font-size: 14px;
    font-weight: 600;
  }
  .block {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .block .label {
    padding: 0 8px;
  }
  .group {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px;
    border-radius: 18px;
    background: var(--surface-2);
  }
  .item {
    display: flex;
    align-items: center;
    gap: 14px;
    min-height: 54px;
    padding: 0 12px;
    border-radius: var(--r-md);
    font-weight: 600;
    font-size: 15px;
    text-align: left;
    transition: background var(--dur-tap);
  }
  .item:hover {
    background: rgba(255, 255, 255, 0.06);
  }
  .item :global(svg) {
    color: var(--text-2);
  }
  .item.static:hover {
    background: none;
  }
  .item.danger,
  .item.danger :global(svg) {
    color: var(--danger);
  }
  .dot {
    display: inline-block;
    width: 7px;
    height: 7px;
    margin-right: 6px;
    border-radius: 50%;
    background: var(--accent-3);
    vertical-align: 1px;
  }
  .dot.off {
    background: var(--accent-2);
  }
  .grow {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  small {
    font-size: 12px;
    font-weight: 600;
  }
  .swatch {
    width: 34px;
    height: 34px;
    border-radius: var(--r-sm);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .pad {
    padding: 8px 12px;
    margin: 0;
    font-size: 14px;
  }
  .logout {
    align-self: flex-start;
  }
  .form {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 0 8px;
  }
  .form h2 {
    font-size: 22px;
  }
  .actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    align-items: center;
  }
  .emoji {
    width: 90px;
    font-size: 22px;
  }
  .palette {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .color {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    transition: transform var(--dur-base) var(--ease-pop);
  }
  .color[aria-pressed="true"] {
    transform: scale(1.18);
    box-shadow:
      0 0 0 3px #1c1c21,
      0 0 0 5px var(--text);
  }
</style>
